import LayerGroup from 'ol/layer/Group';
import DrawFeatures, { DrawFeaturesEventType } from '../../layers/vector/DrawFeatures'
import EditableFeature from './EditableFeature';
import {
  CIRCLE_USER_GEOMETRY,
  AREA_USER_GEOMETRY,
  LINE_USER_GEOMETRY,
  POINT_USER_GEOMETRY
} from './AbstractEditFeatureModule';

// The object build instruction attribute name.
export const OBJECT_BUILD_INSTRUCTION_ATTRIBUTE_NAME = 'Input identifier';

/**
 * This class manage the vector layer of the AddFeatureWFS control. It manages
 * the edition layer.
 * @extends {LayerGroup}
 */
export default class EditFeatureLayer extends LayerGroup {
  /**
     * @constructor
     * @param {import("../../map").default} map
     */
  constructor(map, options = {}) {
    super();

    /**
         * The map geomod parser.
         * @private
         * @type {import("ol/format/GeoJSON").default}
         */
    this.geojson_ = map.getGeoJSONParser();

    /**
         * Layer where route edition layer is drawn.
         * @protected
         * @type {DrawFeatures}
         */
    this.featureEditionLayer_ = new DrawFeatures({ map, ...options });
    this.getLayers().push(this.featureEditionLayer_);
    this.featureEditionLayer_.on(DrawFeaturesEventType.DRAW_START, (event) => this.onDrawStart_(event));
    this.featureEditionLayer_.on(DrawFeaturesEventType.DRAW_END, (event) => this.onDrawEnd_(event));
    this.featureEditionLayer_.on(DrawFeaturesEventType.SELECTED_POINT, (e) => this.dispatchEvent(e));
    this.featureEditionLayer_.on(DrawFeaturesEventType.UNSELECTED_POINT, (e) => this.dispatchEvent(e));

    /**
         * The editied feature
         * @private
         * @type {EditableFeature}
         */
    this.feature_ = null;
  }

  /**
     * Callback on draw start event.
     * @protected
     * @param {import("../../layers/vector/DrawFeatures").DrawFeaturesEvent} event The draw start event.
     */
  onDrawStart_(event) {
    this.dispatchEvent(event);
  }

  /**
     * Callback on draw end event.
     * @protected
     * @param {import("../../layers/vector/DrawFeatures").DrawFeaturesEvent} event The draw start event.
     */
  onDrawEnd_(event) {
    this.dispatchEvent(event);
  }

  /**
     * Set the feature in the layer. Modify the feature geometry.
     * @param {EditableFeature} feature The parsed ol feature.
     */
  setFeature(feature) {
    this.feature_ = feature;
    this.featureEditionLayer_.setFeature(feature);
  }

  /**
     * Define which geometry will be drawn and what to do when the geometry changes
     * @param {string} mode The type of geometry drawn
     */
  setMode(mode) {
    let realMode;
    if (mode === AREA_USER_GEOMETRY) {
      realMode = 'Polygon';
    } else if (mode === LINE_USER_GEOMETRY) {
      realMode = 'LineString';
    } else if (mode === POINT_USER_GEOMETRY) {
      realMode = 'Point';
    } else if (mode === CIRCLE_USER_GEOMETRY) {
      realMode = 'Circle';
    }

    if (realMode) {
      this.featureEditionLayer_.startEdition(realMode);
    }

    this.featureEditionLayer_.clear();
  }

  clear() {
    this.featureEditionLayer_.stop();
    this.featureEditionLayer_.clear();
  }

  /**
     * Getter the edited feature.
     * @return {EditableFeature} The current edited feature.
     */
  getCreatedFeature() {
    let feature = this.featureEditionLayer_.getFeature();
    if (feature) {
      feature = EditableFeature.toEditableFeature(feature);
    }
    return feature;
  }

  /**
     * @return {import('ol/geom').Geometry}
     */
  getGeometry() {
    const feature = this.featureEditionLayer_.getFeature();
    let geom;
    if (feature) {
      geom = feature.getGeometry();
    }
    return geom;
  }
}
